import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Backdrop,
  CircularProgress,
  IconButton,
  Tooltip,
  tooltipClasses,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { withStyles } from "@mui/styles";
import LastPageIcon from "@mui/icons-material/LastPage";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import { useAction } from "../../../../hooks/actions";
import { TooltipProps } from "../../../../helpers/materialUITheme";
import InputText from "../../../components/InputText";
import FormItem from "../../../components/FormItem";
import TaskStateList from "../../../components/TaskStateList/TaskStateList";
import DonutChart from "../../../components/DonutChart";
import "./styles/style.css";

const TableCellItem = ({ mode = null, data, value, floorModalHandler, color, setIsPollingActive }) => {
  const navigate = useNavigate();

  switch (mode) {
    case "diagram":
      const { messages } = data || {};

      const HtmlTooltip = styled(({ className, ...props }) => (
        <Tooltip {...props} classes={{ popper: className }} componentsProps={TooltipProps} />
      ))(() => ({
        [`& .${tooltipClasses.tooltip}`]: {
          padding: "16px",
        },
      }));

      const handleMouseEnter = (event) => {
        event.stopPropagation();
        setIsPollingActive(false);
      };

      // Запуск опроса при уходе мыши
      const handleMouseLeave = (event) => {
        event.stopPropagation();
        setIsPollingActive(true);
      };

      return (
        <Box onMouseEnter={(e) => handleMouseEnter(e)} onMouseLeave={(e) => handleMouseLeave(e)}>
          <HtmlTooltip
            arrow
            placement="left-start"
            title={Object.values(messages || {})?.length ? <TaskStateList data={messages || []} /> : ""}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <DonutChart data={messages || []} width={36} height={36} radius={18} />
            </Box>
          </HtmlTooltip>
        </Box>
      );
    case "list":
      return (
        <ul style={{ listStyleType: "none", padding: 0, margin: 0 }}>
          {value.map(({ floorId, floor }, i) => {
            return (
              <li
                key={floorId}
                style={{
                  color: color ? color : null,
                  marginTop: i !== 0 ? "8px" : null,
                }}
                onClick={() => floorModalHandler(floorId)}
              >
                {floor}
              </li>
            );
          })}
        </ul>
      );
    case "linkedList":
      return (
        <List>
          {value.map(({ cellText, link }, i) => {
            return (
              <ListItem key={i} sx={{ p: 0 }}>
                <ListItemButton
                  className="table-row-cell-link"
                  sx={{ verticalAlign: "initial", pt: 0, pb: 0 }}
                  onClick={() => navigate(link)}
                >
                  <ListItemText primary={cellText} />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      );
    case "workList":
      return (
        <List>
          {value.map(({ id, description, name }) => {
            return (
              <ListItem key={id} sx={{ p: 0 }}>
                <ListItemText primary={description || name} />
              </ListItem>
            );
          })}
        </List>
      );
    case "simpleList":
      return (
        <List>
          {value.map((item, i) => {
            return (
              <ListItem key={i} sx={{ p: 0 }}>
                <ListItemText primary={item} />
              </ListItem>
            );
          })}
        </List>
      );
    case "sign":
      return (
        value && (
          <Box sx={{ height: "30px" }}>
            <img style={{ height: "30px" }} alt="sign" src="/sign.png" />
          </Box>
        )
      );
    case "edit":
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {value}
        </Box>
      );
    default:
      return <Box sx={{ color: color ? color : null }}>{value}</Box>;
  }
};

const BorderlessTableCell = (props) => {
  // Предположим, что у вас есть переменная shouldApplyStyles, определяющая, когда нужно применять стили
  const shouldApplyStyles = props.shouldApplyStyles;

  const StyledTableCell = shouldApplyStyles
    ? withStyles((theme) => ({
        root: {
          borderTop: (props) => `1px solid ${props.border || "black"}`,
          borderBottom: "none", // Установите стиль и цвет границы ячейки
          borderLeft: (props) => (props.colIndex == 0 ? `1px solid ${props.border || "black"}` : "none"),
          borderRight: (props) =>
            props.colIndex == props.length - 1 ? `1px solid ${props.border || "black"}` : "none",
        },
      }))(TableCell)
    : TableCell;

  return <StyledTableCell {...props} />;
};

const TableBasic = ({
  renderHeader,
  columns,
  rows,
  handleClickCell = null,
  customCountPagination = 15,
  floorModalHandler = null,
  pageNumber,
  changePageNumber = () => {},
  isLoading,
  isPagination = true,
  count,
  getCustomBgColorRow,
  getCustomBorderRow,
  minHeight = "55vh",
  maxHeight = "60vh",
  isNotBigTable,
  savePageNumberToSessionStorage = () => {},
  setIsChoosenFirstPage,
  jobChartData,
  setIsPollingActive,
}) => {
  const { t } = useTranslation();
  const { changeRowsPerPage } = useAction();
  const { control, setValue } = useForm();

  const handleChangePage = (event, newPage) => {
    changePageNumber(newPage);
    savePageNumberToSessionStorage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    changeRowsPerPage(parseInt(event.target.value, 10));
    changePageNumber(0);
    savePageNumberToSessionStorage(0);
  };

  useEffect(() => {
    changePageNumber(pageNumber);
    setValue("paginationTextField", pageNumber + 1);
    savePageNumberToSessionStorage(pageNumber);
  }, [rows]);

  useEffect(() => () => setValue("paginationTextField", pageNumber + 1), []);

  const handleLastPageButtonClick = () => {
    const newPage = Math.max(0, Math.ceil(count / customCountPagination) - 1);
    changePageNumber(newPage);
    savePageNumberToSessionStorage(newPage);
    setValue("paginationTextField", newPage);
  };

  const handleFirstPageButtonClick = () => {
    setIsChoosenFirstPage && setIsChoosenFirstPage(true);
    changePageNumber(0);
    setValue("paginationTextField", 1);
    savePageNumberToSessionStorage(0);
  };

  const handlePrevPageButtonClick = () => {
    changePageNumber((prev) => {
      if (Number(prev) - 1 === 0) {
        setIsChoosenFirstPage && setIsChoosenFirstPage(true);
      }
      return Number(prev) - 1;
    });
    savePageNumberToSessionStorage(pageNumber);
    setValue("paginationTextField", Number(pageNumber));
  };

  const handleNextPageButtonClick = () => {
    changePageNumber((prev) => Number(prev) + 1);
    savePageNumberToSessionStorage(pageNumber + 1);
    setValue("paginationTextField", Number(pageNumber) + 2);
  };

  const handleKeyPress = async (event) => {
    if (event.key === "Enter") {
      if (Number(event.target.value) > Math.max(0, Math.ceil(count / customCountPagination) - 1)) {
        const numberOfPage = Math.max(0, Math.ceil(count / 15) - 1);
        changePageNumber(numberOfPage);
        savePageNumberToSessionStorage(numberOfPage);
        setValue("paginationTextField", numberOfPage + 1);
      } else if (event.target.value == "" || event.target.value == "0") {
        changePageNumber(0);
        savePageNumberToSessionStorage(0);
        setValue("paginationTextField", 1);
      } else {
        changePageNumber(Number(event.target.value) - 1);
        savePageNumberToSessionStorage(Number(event.target.value) - 1);
      }
    } else {
      if (
        event.key === "e" ||
        event.key === "E" ||
        event.key === "*" ||
        event.key === "/" ||
        event.key === "-" ||
        event.key === "+" ||
        event.key === "." ||
        event.key === ","
      ) {
        event.preventDefault();
      } else {
        const input = event.target.value;
        const filteredInput = input.replace(/[^\d]/g, "").slice(0, 4);
        setValue("paginationTextField", filteredInput);
      }
    }
  };

  return (
    <Box className={`table_field ${isNotBigTable ? "table-without-padding" : ""}`}>
      <Backdrop sx={{ color: "var(--white)", zIndex: 9999 }} open={isLoading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Paper className="paper" sx={{ mb: 2 }}>
        <TableContainer
          sx={{
            borderRadius: "2px 2px",
            maxHeight: () => (window.innerHeight > 715 ? maxHeight : minHeight),
          }}
        >
          <Table sx={{ minWidth: 750 }} className="table" aria-labelledby="tableTitle" size={"medium"} stickyHeader>
            {renderHeader}
            {!isLoading && (
              <TableBody className="table-body">
                {rows.map((row, index) => (
                  <TableRow key={index} hover tabIndex={-1}>
                    {columns.map(
                      (
                        { id, clickable, mode = null, color = null, styleTableCell, sign = false, isSticky, RowCell },
                        colIndex
                      ) => (
                        <BorderlessTableCell
                          key={id}
                          shouldApplyStyles={getCustomBorderRow ? true : false}
                          border={getCustomBorderRow ? getCustomBorderRow(row, index) : ""}
                          colIndex={colIndex}
                          length={columns?.length}
                          className={`table-row-cell ${isSticky ? "sticky" : ""}`}
                          onClick={(event) => clickable && handleClickCell(event, row.id)}
                          sx={{
                            cursor: clickable ? "pointer" : "default",
                            // border: getCustomBorderRow ? "4px solid "+getCustomBorderRow(row, index) : 0,
                            background: getCustomBgColorRow
                              ? getCustomBgColorRow(row, index)
                              : index % 2
                                ? "var(--bgTableRow)"
                                : "var(--bgWhite)",
                            ...styleTableCell,
                          }}
                        >
                          {RowCell ? (
                            <RowCell id={row.id} value={row[id]} item={row[id]} row={row} index={index} />
                          ) : (
                            <TableCellItem
                              data={row}
                              mode={mode}
                              value={row[id]}
                              floorModalHandler={floorModalHandler}
                              color={color}
                              setIsPollingActive={setIsPollingActive}
                            />
                          )}
                        </BorderlessTableCell>
                      )
                    )}
                  </TableRow>
                ))}
                {rows?.length === 0 && (
                  <TableRow
                    style={{
                      height: 56,
                    }}
                  >
                    <TableCell sx={{ textAlign: "center" }} colSpan={columns?.length}>
                      {t("table:empty_result")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            )}
          </Table>
        </TableContainer>

        {!!rows?.length && isPagination && (
          <Box sx={{ position: "relative" }}>
            {jobChartData && (
              <Box sx={{ position: "absolute", bottom: "15px", left: "0", display: "flex", gap: "32px" }}>
                <Typography sx={{ fontSize: "16px", fontWeight: "500", color: "var(--mainText)" }}>
                  Всего {jobChartData.totalJobsAmount}
                </Typography>
                <Box sx={{ display: "flex", gap: "16px" }}>
                  <Box sx={{ display: "flex", gap: "11px", alignItems: "center" }}>
                    <Box
                      sx={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: "var(--error)" }}
                    ></Box>
                    <Typography
                      sx={{ fontSize: "16px", fontWeight: "500", color: "var(--mainText)" }}
                      className="jobChartDote"
                    >
                      {jobChartData.overduedJobsAmount}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", gap: "11px", alignItems: "center" }}>
                    <Box sx={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: "#FF8A00" }}></Box>
                    <Typography
                      sx={{ fontSize: "16px", fontWeight: "500", color: "var(--mainText)" }}
                      className="jobChartDote"
                    >
                      {jobChartData.nearDeadlineJobsAmount}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", gap: "11px", alignItems: "center" }}>
                    <Box sx={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: "#FBE312" }}></Box>
                    <Typography
                      sx={{ fontSize: "16px", fontWeight: "500", color: "var(--mainText)" }}
                      className="jobChartDote"
                    >
                      {jobChartData.notNearDeadlineJobsAmount}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", gap: "11px", alignItems: "center" }}>
                    <Box sx={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: "#21AC21" }}></Box>
                    <Typography
                      sx={{ fontSize: "16px", fontWeight: "500", color: "var(--mainText)" }}
                      className="jobChartDote"
                    >
                      {jobChartData.completedInTimeJobsAmount}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
            <TablePagination
              rowsPerPageOptions={[10]}
              component="div"
              count={count || rows.length}
              rowsPerPage={customCountPagination}
              page={pageNumber}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage={t("table:labelRowsPerPage")}
              labelDisplayedRows={({ from, to, count }) => {
                return (
                  <div style={{ fontSize: "16px" }}>{`${from > to ? to : from}–${to} из ${
                    count !== -1 ? (count > 10000 ? `10000+` : count) : `больше чем ${to}`
                  }`}</div>
                );
              }}
              ActionsComponent={(subprops) => {
                return (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <IconButton
                      onClick={handleFirstPageButtonClick}
                      aria-label="last page"
                      sx={{ height: "40px" }}
                      disabled={pageNumber === 0}
                    >
                      <FirstPageIcon />
                    </IconButton>
                    <IconButton
                      onClick={handlePrevPageButtonClick}
                      sx={{ height: "40px" }}
                      disabled={pageNumber === 0}
                      aria-label="last page"
                    >
                      <NavigateBeforeIcon />
                    </IconButton>
                    <FormItem
                      control={control}
                      name={`paginationTextField`}
                      placeholder="Страница"
                      size="small"
                      defaultValue={pageNumber || 1}
                      sx={{ width: "100px" }}
                      type={"number"}
                      InputProps={{ type: "number" }}
                      sx={{
                        "& .MuiInputBase-root": {
                          padding: "10px",
                        },
                        "& input[type=number]": {
                          "-moz-appearance": "textfield",
                          width: "100%",
                          textAlign: "center",
                        },
                      }}
                      input={(props) => <InputText onKeyPress={handleKeyPress} {...props} />}
                    />

                    <IconButton
                      onClick={handleNextPageButtonClick}
                      sx={{ height: "40px" }}
                      disabled={pageNumber === Math.max(0, Math.ceil(count / customCountPagination) - 1)}
                      aria-label="last page"
                    >
                      <NavigateNextIcon />
                    </IconButton>
                    <IconButton
                      onClick={handleLastPageButtonClick}
                      sx={{ height: "40px" }}
                      disabled={pageNumber === Math.max(0, Math.ceil(count / customCountPagination) - 1)}
                      aria-label="last page"
                    >
                      <LastPageIcon />
                    </IconButton>
                  </Box>
                );
              }}
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default TableBasic;
